import { useRef, useState } from "react";
import { Camera, Clock, Loader2, RotateCcw, Move } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import PhotoAdjustModal from "@/components/media/photo_adjust_modal";

// Circular avatar upload used by both profile pages. Picking a file opens
// the pan/zoom adjuster first; Save exports the framed square and uploads
// it to /users/me/photo. The saved photo can be re-adjusted any time via
// the Adjust button (same-origin /uploads, so canvas export is allowed).
export default function PhotoUpload({ value, onChange, error, label = "Profile photo" }) {
  const inputRef = useRef(null);
  const lastFileRef = useRef(null);
  // Object URLs we created, so each can be revoked when the NEXT one
  // replaces it. Never revoked earlier: the rule is a URL is only released
  // once nothing on screen can still be rendering it.
  const adjustUrlRef = useRef(null); // blob: URL feeding the adjuster
  const previewUrlRef = useRef(null); // blob: URL handed to onChange
  const { setUser } = useAuth();
  const [progress, setProgress] = useState(null); // null | 0..100
  const [failed, setFailed] = useState(false);
  const [pending, setPending] = useState(false); // verified: awaiting review
  const [adjustSrc, setAdjustSrc] = useState(null); // object URL being framed

  async function upload(file) {
    setFailed(false);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/users/me/photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.min(99, Math.round((e.loaded / e.total) * 100)));
        },
      });
      const photoUrl = res.data?.photo_url;
      // Verified accounts do not swap faces silently: the backend parks the
      // new photo for admin review and answers pending:true with the OLD
      // url — keep showing the current photo and say what is happening.
      if (res.data?.pending) {
        setPending(true);
        if (photoUrl) onChange?.(photoUrl); // revert the local blob preview
        return;
      }
      setPending(false);
      if (photoUrl) {
        onChange?.(photoUrl);
        // Persist into the session user so every surface (and a reload)
        // shows the saved photo, not just this form's local preview.
        setUser((u) => (u ? { ...u, photo_url: photoUrl } : u));
      }
    } catch (err) {
      if (err?.response) {
        // The backend rejected the upload — keep the preview, offer retry.
        setFailed(true);
      }
      // No response means offline browsing: the local preview stays usable
      // and we retry silently next time the user picks a photo.
    } finally {
      setProgress(null);
    }
  }

  function onFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    // WHY the revoke: the previous adjuster URL is off screen by the time a
    // new file replaces it, so releasing it here frees the blob without
    // ever breaking an image that could still be rendering.
    if (adjustUrlRef.current) URL.revokeObjectURL(adjustUrlRef.current);
    adjustUrlRef.current = URL.createObjectURL(file);
    // The adjuster opens FIRST; upload happens on Save with the framed crop.
    setAdjustSrc(adjustUrlRef.current);
    e.target.value = "";
  }

  async function onAdjustSave(blob) {
    const file = new File([blob], "photo.jpg", { type: "image/jpeg" });
    lastFileRef.current = file;
    setAdjustSrc(null);
    // WHY the revoke: the old preview leaves the screen the instant the new
    // one replaces it in the avatar, so the PREVIOUS URL is safe to release.
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = URL.createObjectURL(blob);
    onChange?.(previewUrlRef.current); // instant preview
    await upload(file);
  }

  const uploading = progress != null;

  return (
    <div className="flex items-center gap-4">
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        aria-label={value ? "Change your profile photo" : "Add a profile photo"}
        className={cn(
          "relative flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 bg-neutral-50 transition",
          value ? "border-brand-line hover:border-brand-primary" : "border-dashed",
          !value && (error ? "border-red-500 text-red-600" : "border-brand-line text-brand-secondary hover:border-brand-primary hover:text-brand-lime")
        )}
      >
        {value ? (
          <img src={value} alt="Your profile" className="h-full w-full object-cover" />
        ) : (
          <span className="flex flex-col items-center gap-1">
            <Camera size={20} />
            <span className="text-[10px] font-semibold">Add photo</span>
          </span>
        )}
        {uploading && (
          <span className="absolute inset-0 flex flex-col items-center justify-center gap-1 rounded-full bg-white/75">
            <Loader2 size={18} className="animate-spin text-brand-lime" />
            <span className="text-[10px] font-semibold text-brand-ink">{progress}%</span>
          </span>
        )}
      </button>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-brand-ink">
          {label} <span className="font-medium text-brand-secondary">(required)</span>
        </p>
        <p className="mt-0.5 text-xs leading-relaxed text-brand-secondary">
          A clear photo of you. Profiles with a real photo get far more matches.
        </p>
        {pending && !uploading && (
          <span className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
            <Clock size={11} /> New photo awaiting approval
          </span>
        )}
        {value && !uploading && (
          <button
            type="button"
            onClick={() => setAdjustSrc(value)}
            className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-brand-tint px-3 py-1 text-xs font-semibold text-brand-lime transition hover:bg-brand-primary/15"
          >
            <Move size={11} /> Adjust photo
          </button>
        )}
        {failed && (
          <button
            type="button"
            onClick={() => lastFileRef.current && upload(lastFileRef.current)}
            className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700 transition hover:bg-amber-100"
          >
            <RotateCcw size={11} /> Upload did not finish. Tap to retry
          </button>
        )}
        {error && !failed ? <p className="mt-1 text-xs font-semibold text-red-600">{error}</p> : null}
      </div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onFile} />
      <PhotoAdjustModal
        open={!!adjustSrc}
        src={adjustSrc}
        onCancel={() => setAdjustSrc(null)}
        onSave={onAdjustSave}
      />
    </div>
  );
}
